import type { FxSource } from '../types';
import rbaDailyRates from '../data/rbaDailyRates.json';

/**
 * FX rates for a SPECIFIC DATE, which is what both the portfolio and the
 * Australian CGT report actually need — not today's spot rate.
 *
 * ── The chain ────────────────────────────────────────────────────────────────
 *
 *   1. RBA  — `src/data/rbaDailyRates.json`, the RBA's daily F11.1 table
 *             converted at build time (see scripts/buildRbaRates.js). Bundled,
 *             so it answers synchronously and works offline. This is the
 *             primary source because the ATO's own guidance points at the RBA
 *             for AUD conversions.
 *   2. ECB  — Frankfurter (ECB reference rates), for dates the bundle does not
 *             cover: anything after the last build, before 2010, or a currency
 *             the RBA has stopped publishing (CAD, discontinued from 2023).
 *             Reached through `api/fx-history.js` in production.
 *   3. MANUAL — a caller-supplied rate, used only when both feeds fail. Nothing
 *             is fabricated: with no manual rate, resolution returns null and
 *             the caller records the entry pair WITHOUT rates, exactly as the
 *             spot-capture path already does.
 *
 * ── Orientation ──────────────────────────────────────────────────────────────
 *
 * Everything in here — the bundle, the cache, and the proxy's response — is
 * stored in the RBA's own orientation: `A$1 = X`, i.e. UNITS OF X PER ONE AUD.
 * One orientation end-to-end means exactly one inversion, in `ratesFromPerAud`,
 * instead of a conversion at every boundary. The RBA publishes `A$1=USD`, so
 * the number you want for "AUD per USD" is its RECIPROCAL — inverting in the
 * wrong place is the single easiest way to get this module subtly, plausibly
 * wrong, so it happens once and is asserted by the tests.
 *
 * ── Weekends and holidays ────────────────────────────────────────────────────
 *
 * Neither the RBA nor the ECB publishes on weekends or public holidays, but
 * people trade crypto on them. A date with no published rate ROLLS BACK to the
 * most recent prior business day (`MAX_ROLLBACK_DAYS` of slack, enough for the
 * Christmas–New Year and Easter closures). The date actually used is returned
 * as `fxRateDate`, which is why that field exists separately from the
 * transaction's own `date`.
 */

// ── Bundled RBA table ─────────────────────────────────────────────────────────

interface RbaTable {
  columns: string[];
  firstDate: string;
  lastDate: string;
  rates: Record<string, (number | null)[]>;
}

const RBA = rbaDailyRates as unknown as RbaTable;
const RBA_COL: Record<string, number> = Object.fromEntries(
  RBA.columns.map((code, i) => [code, i])
);

/** How many calendar days to walk back looking for a published rate. */
const MAX_ROLLBACK_DAYS = 10;

// ── localStorage cache ────────────────────────────────────────────────────────

const CACHE_KEY = 'mycryptoplanner_fx_history';
/** Bounded so a long history can never bloat localStorage past its quota. */
const CACHE_MAX_DATES = 750;

interface FxHistoryCache {
  v: 1;
  /** date → { CODE: units of CODE per 1 AUD }. Same orientation as the bundle. */
  entries: Record<string, Record<string, number>>;
  /** Insertion order, oldest first — used to evict past CACHE_MAX_DATES. */
  order: string[];
}

let cache: FxHistoryCache | null = null;

function loadCache(): FxHistoryCache {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as FxHistoryCache;
      if (parsed && parsed.v === 1 && parsed.entries) {
        cache = { v: 1, entries: parsed.entries, order: parsed.order ?? Object.keys(parsed.entries) };
        return cache;
      }
    }
  } catch {
    /* unreadable or unparseable cache — start fresh rather than throw */
  }
  cache = { v: 1, entries: {}, order: [] };
  return cache;
}

function saveCache(c: FxHistoryCache): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(c));
  } catch {
    /* quota or private mode — the cache is an optimisation, never a requirement */
  }
}

function cachePut(date: string, perAud: Record<string, number>): void {
  const c = loadCache();
  if (!c.entries[date]) c.order.push(date);
  c.entries[date] = { ...c.entries[date], ...perAud };
  while (c.order.length > CACHE_MAX_DATES) {
    const evicted = c.order.shift();
    if (evicted) delete c.entries[evicted];
  }
  saveCache(c);
}

/** Drop the cached history. Exposed for tests and for a manual "refresh rates". */
export function clearFxHistoryCache(): void {
  cache = null;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}

// ── Date helpers ──────────────────────────────────────────────────────────────

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Today as 'YYYY-MM-DD' in LOCAL time, matching how the entry forms default
 * their date field.
 *
 * Defined here rather than in fxCapture so the dependency runs one way only
 * (fxCapture → fxRates); fxCapture re-exports it for its existing callers.
 */
export function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Normalise any ISO-ish string to a bare 'YYYY-MM-DD', or null if it isn't one. */
export function isoDateOnly(iso: string): string | null {
  const m = ISO_RE.exec(iso ?? '');
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

/**
 * `date` shifted by `days`, as 'YYYY-MM-DD'.
 *
 * Built on Date.UTC rather than local time deliberately: UTC has no daylight
 * saving, so stepping a day is always exactly a day. The same reasoning as
 * `dayNumber` in cgtCalculator.
 */
function shiftDays(date: string, days: number): string {
  const m = ISO_RE.exec(date);
  if (!m) return date;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) + days * 86_400_000;
  return new Date(ms).toISOString().slice(0, 10);
}

/** Saturday or Sunday, evaluated in UTC (see shiftDays). Exposed for tests. */
export function isWeekend(date: string): boolean {
  const m = ISO_RE.exec(date);
  if (!m) return false;
  const day = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
  return day === 0 || day === 6;
}

// ── Orientation: per-AUD → the app's two legs ─────────────────────────────────

/**
 * The resolved rates for one currency on one date.
 *
 * Mirrors `ResolvedRates` in fxCapture, but `fxSource` is always a date-correct
 * source — never `spot-at-entry`, which is the whole point of this module.
 */
export interface DatedRates {
  /** USD per 1 unit of the requested currency. */
  fxRateUsd: number;
  /** AUD per 1 unit of the requested currency. */
  fxRateAud?: number;
  /** The date the rates are genuinely for — possibly rolled back from the ask. */
  fxRateDate: string;
  fxSource: Exclude<FxSource, 'spot-at-entry'>;
}

function usable(n: number | null | undefined): n is number {
  return typeof n === 'number' && isFinite(n) && n > 0;
}

/**
 * Turn a row of `A$1 = X` quotes into the app's two legs for `code`.
 *
 * This is the module's ONE inversion (see the orientation note at the top):
 *   - AUD per unit of X  = 1 / (X per AUD)
 *   - USD per unit of X  = (USD per AUD) / (X per AUD)
 * AUD and USD short-circuit to their own identities so a USD entry still
 * resolves on a day the USD column is blank (US public holidays — the WM/Reuters
 * fixing skips them while the rest of the row is published).
 */
function ratesFromPerAud(
  code: string,
  perAud: Record<string, number | null | undefined>,
  fxRateDate: string,
  fxSource: DatedRates['fxSource']
): DatedRates | null {
  const usdPerAud = perAud.USD;

  // Units of `code` per 1 AUD. AUD is its own base and never appears as a column.
  const codePerAud = code === 'AUD' ? 1 : perAud[code];
  if (!usable(codePerAud)) return null;

  const fxRateAud = 1 / codePerAud;
  const fxRateUsd =
    code === 'USD' ? 1 : usable(usdPerAud) ? usdPerAud / codePerAud : undefined;

  // The USD leg is what the app stores prices in, so a row that cannot produce
  // it is no use here — better to fall through to the next source in the chain.
  if (!usable(fxRateUsd)) return null;

  return { fxRateUsd, fxRateAud, fxRateDate, fxSource };
}

// ── Source 1: the bundled RBA table (synchronous) ─────────────────────────────

/** The RBA row for `date` as a { CODE: per-AUD } map, or null if not published. */
function rbaRow(date: string): Record<string, number> | null {
  const row = RBA.rates[date];
  if (!row) return null;
  const out: Record<string, number> = {};
  for (const [code, i] of Object.entries(RBA_COL)) {
    const v = row[i];
    if (usable(v)) out[code] = v;
  }
  return Object.keys(out).length ? out : null;
}

/** The cached row for `date` (from a previous proxy call), or null. */
function cachedRow(date: string): Record<string, number> | null {
  const entry = loadCache().entries[date];
  return entry && Object.keys(entry).length ? entry : null;
}

/**
 * Walk back from `day` to the most recent date that yields a COMPLETE answer for
 * `code`, within `MAX_ROLLBACK_DAYS`.
 *
 * Weekends and public holidays are handled by the same mechanism: a holiday is
 * simply a weekday the source did not publish, so there is no holiday calendar
 * to maintain and get out of date.
 *
 * The test is "does this row actually resolve", not "does this row exist".
 * Those differ: F11.1 publishes rows where an individual column is blank (the
 * USD column skips US public holidays even though the RBA published that day,
 * and CAD stops entirely from 2023). A row like that must be stepped over, not
 * accepted and then found wanting — accepting it would abandon the roll-back
 * and drop the lookup through to a slower source, or to nothing at all.
 */
function resolveByRollBack(
  code: string,
  day: string,
  getRow: (d: string) => Record<string, number> | null,
  source: DatedRates['fxSource']
): DatedRates | null {
  for (let i = 0; i <= MAX_ROLLBACK_DAYS; i++) {
    const candidate = shiftDays(day, -i);
    const row = getRow(candidate);
    if (!row) continue;
    const resolved = ratesFromPerAud(code, row, candidate, source);
    if (resolved) return resolved;
  }
  return null;
}

/**
 * Resolve `currency` on `date` from data already on hand — the bundled RBA table
 * and the localStorage cache — with no network call.
 *
 * Synchronous by design: the CGT engine runs inside a `useMemo` and cannot await,
 * so callers warm the cache first (`warmRatesForDates`) and then read with this.
 */
export function lookupRatesSync(currency: string, date: string): DatedRates | null {
  const code = (currency || '').toUpperCase();
  const day = isoDateOnly(date);
  if (!code || !day) return null;

  // The bundle is authoritative where it reaches, and ONLY where it reaches.
  //
  // The roll-back exists for weekends and public holidays inside a published
  // series. Applied to a date after `RBA.lastDate` it would walk back into the
  // bundle and answer with its LAST rate, up to MAX_ROLLBACK_DAYS stale, for the
  // first week and a half after every bundle build. It once did: 24 Sep to 3 Oct
  // 2026 all converted at 23 Sep's rate, up to 2.4% off for AUD, in the CGT
  // report as well as on the charts. A date after the bundle is answered from the
  // ECB cache or not at all, so callers warm it (warmRatesForDates) or carry the
  // last known rate forward (carryForward) and flag that they did.
  const bundle = day <= RBA.lastDate ? resolveByRollBack(code, day, rbaRow, 'rba') : null;
  return bundle ?? resolveByRollBack(code, day, cachedRow, 'ecb');
}

/** How far back a carry-forward may reach before giving up. */
export const MAX_CARRY_DAYS = 400;

/**
 * The most recent answer `resolve` gives for a date BEFORE `date`, walking back
 * one day at a time, with the date it came from.
 *
 * The fallback for a date nothing resolves, used by the CGT engine and the
 * display tables alike: "unchanged since the last rate we have" is a far better
 * estimate than jumping to today's spot, and it never puts an artificial step in
 * a series. Callers must still FLAG the result as estimated; this only finds it.
 * Null if nothing is found within MAX_CARRY_DAYS (then: spot, also flagged).
 */
export function carryForward<T>(
  date: string,
  resolve: (day: string) => T | null | undefined,
  maxDays: number = MAX_CARRY_DAYS,
): { value: T; date: string } | null {
  const start = isoDateOnly(date);
  if (!start) return null;
  let d = start;
  for (let i = 0; i < maxDays; i++) {
    d = shiftDays(d, -1);
    const value = resolve(d);
    if (value != null) return { value, date: d };
  }
  return null;
}

/**
 * AUD per 1 USD on `date`, from data already on hand.
 *
 * The single number the CGT engine needs for every legacy row: those store a USD
 * price and nothing else, so this is what turns them into AUD on their own date.
 */
export function audPerUsdSync(date: string): { rate: number; rateDate: string; source: 'rba' | 'ecb' } | null {
  const resolved = lookupRatesSync('USD', date);
  if (!resolved || !usable(resolved.fxRateAud)) return null;
  return {
    rate: resolved.fxRateAud,
    rateDate: resolved.fxRateDate,
    source: resolved.fxSource === 'rba' ? 'rba' : 'ecb',
  };
}

/** Whether the bundled RBA table covers `date` at all (before any roll-back). */
export function bundleCovers(date: string): boolean {
  const day = isoDateOnly(date);
  if (!day) return false;
  return day >= RBA.firstDate && day <= RBA.lastDate;
}

/** The span of the bundled table, for diagnostics and UI copy. */
export const RBA_COVERAGE = { firstDate: RBA.firstDate, lastDate: RBA.lastDate };

// ── Source 2: Frankfurter (ECB), via the proxy ────────────────────────────────

/** ECB reference rates, keyless and CORS-open — see api/fx-history.js. */
const FRANKFURTER_BASE = 'https://api.frankfurter.dev/v1';
const FX_PROXY = '/api/fx-history';

/**
 * Production routes through the Vercel proxy (edge-cached, one shared result);
 * `npm start` doesn't serve /api, so local dev calls Frankfurter directly, which
 * is CORS-open. Same split, and same reasoning, as `isLocalDev` in coingecko.ts.
 */
function isLocalDev(): boolean {
  if (typeof window !== 'undefined' && window.location) {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0') return true;
    if (h === '' && window.location.protocol === 'file:') return true;
  }
  return process.env.NODE_ENV !== 'production';
}

/** Currencies requested from Frankfurter: the app's set, minus the AUD base. */
const WANTED = ['USD', 'EUR', 'GBP', 'JPY', 'CAD'];

interface FrankfurterResponse {
  date?: string;
  base?: string;
  rates?: Record<string, number>;
}

/**
 * Fetch one date's rates (AUD-based) and cache them.
 *
 * Frankfurter performs its own roll-back — asked for a Saturday it answers with
 * Friday's rates AND says so in `date`, which we honour rather than re-deriving.
 */
async function fetchFromFrankfurter(date: string, signal?: AbortSignal): Promise<string | null> {
  const qs = `base=AUD&symbols=${WANTED.join(',')}`;
  const url = isLocalDev()
    ? `${FRANKFURTER_BASE}/${date}?${qs}`
    : `${FX_PROXY}?date=${encodeURIComponent(date)}&${qs}`;

  const res = await fetch(url, { headers: { Accept: 'application/json' }, signal });
  if (!res.ok) return null;

  const data = (await res.json()) as FrankfurterResponse;
  const rates = data && data.rates;
  if (!rates) return null;

  // The date the rates are actually FOR, which may predate the one we asked for.
  const effective = isoDateOnly(data.date ?? '') ?? date;

  const perAud: Record<string, number> = {};
  for (const code of WANTED) {
    if (usable(rates[code])) perAud[code] = rates[code];
  }
  if (!Object.keys(perAud).length) return null;

  // Cached ONLY under the date the rates are actually for — never under the date
  // asked for. Caching a rolled-back answer under the requested Saturday would
  // make the cache assert that a rate was published on that Saturday, and
  // `fxRateDate` would then report the Saturday instead of the Friday it came
  // from. The roll-back in `lookupRatesSync` walks the cache too, so a repeat
  // ask for that weekend still hits without another request.
  cachePut(effective, perAud);

  return effective;
}

// ── The full chain ────────────────────────────────────────────────────────────

export interface ResolveOptions {
  /**
   * Last-resort rate, used only when BOTH feeds fail: AUD per 1 unit of the
   * requested currency. Recorded as `fxSource: 'manual'`, and never overwritten
   * by a later automatic resolution.
   */
  manualFxRateAud?: number;
  /** Matching USD leg for `manualFxRateAud`: USD per 1 unit of the currency. */
  manualFxRateUsd?: number;
  signal?: AbortSignal;
}

/**
 * Resolve `currency` on `date` through the full chain: RBA → Frankfurter →
 * manual.
 *
 * Returns null when nothing resolves, so the caller records the entry pair
 * WITHOUT rates. A missing rate is recoverable later; a fabricated one recorded
 * as fact is not — the same rule the spot-capture path already follows.
 */
export async function resolveRatesForDate(
  currency: string,
  date: string,
  opts: ResolveOptions = {}
): Promise<DatedRates | null> {
  const code = (currency || '').toUpperCase();
  const day = isoDateOnly(date);
  if (!code || !day) return null;

  // 1 + the cache leg of 2: anything already on hand, no network.
  const local = lookupRatesSync(code, day);
  if (local) return local;

  // A future date has no published rate anywhere and never will; asking the
  // network for one just burns a request to get the same nothing back.
  if (day <= todayIso()) {
    // 2: Frankfurter, for dates or currencies the bundle doesn't reach.
    try {
      const effective = await fetchFromFrankfurter(day, opts.signal);
      if (effective) {
        const row = cachedRow(effective);
        if (row) {
          const resolved = ratesFromPerAud(code, row, effective, 'ecb');
          if (resolved) return resolved;
        }
      }
    } catch {
      /* offline, aborted, or proxy down — fall through to the manual leg */
    }
  }

  // 3: the caller's own rate, if they have one.
  if (usable(opts.manualFxRateAud)) {
    const fxRateUsd = usable(opts.manualFxRateUsd)
      ? opts.manualFxRateUsd
      : code === 'USD'
      ? 1
      : undefined;
    if (usable(fxRateUsd)) {
      return {
        fxRateUsd,
        fxRateAud: opts.manualFxRateAud,
        fxRateDate: day,
        fxSource: 'manual',
      };
    }
  }

  return null;
}

/**
 * Pre-fetch every date in `dates` that isn't already covered locally, so a
 * subsequent synchronous pass (the CGT engine) can resolve them all.
 *
 * Resolves to the number of dates newly fetched — 0 means the synchronous view
 * was already complete and nothing needs recomputing.
 */
export async function warmRatesForDates(
  dates: readonly string[],
  opts: { signal?: AbortSignal; currency?: string } = {}
): Promise<number> {
  const code = (opts.currency || 'USD').toUpperCase();

  const missing: string[] = [];
  const seen = new Set<string>();
  const today = todayIso();

  for (const raw of dates) {
    const day = isoDateOnly(raw);
    if (!day || seen.has(day)) continue;
    seen.add(day);
    if (day > today) continue;             // no rate exists for a future date
    if (lookupRatesSync(code, day)) continue;
    missing.push(day);
  }

  let fetched = 0;
  // Sequential on purpose: these are cache misses on a shared, unauthenticated
  // endpoint, and a portfolio's worth of parallel requests is exactly the burst
  // that gets an IP rate-limited. Re-checking inside the loop means one fetch can
  // satisfy several dates that roll back to the same business day.
  for (const day of missing) {
    if (opts.signal?.aborted) break;
    if (lookupRatesSync(code, day)) continue;
    try {
      const effective = await fetchFromFrankfurter(day, opts.signal);
      if (effective) fetched++;
    } catch {
      /* skip this date; the engine falls back to its supplied spot rate */
    }
  }

  return fetched;
}
